import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { findSlotArt, searchSlots } from './BattleShared';
import {
  C, SIDE, slotLabel, fmtMulti, multiColor, fmtAmount, sidesOf, sideScore, rollSchedule,
} from './battleLib';

// ============================================
// Shared pieces of the slot battle UI (page + admin console + OBS widget), in
// the snutzy brand: purple + white, Unbounded display type, gold only on
// money and the call-to-action. The stylesheet (keyframes + the few
// responsive rules inline styles can't do), the case-opening draw strip,
// slot art, the duel card with its momentum bar, the catalog search.
// ============================================

export const BATTLE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@500;700;800;900&family=Outfit:wght@400;500;600;700;800;900&display=swap');
@keyframes sb-pop { 0% { transform: scale(1.18); filter: brightness(1.9); } 100% { transform: scale(1); filter: brightness(1); } }
@keyframes sb-halo { from { opacity: .9; transform: scale(1); } to { opacity: 0; transform: scale(1.35); } }
@keyframes sb-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes sb-drop { from { opacity: 0; transform: translateY(-14px); } to { opacity: 1; transform: none; } }
@keyframes sb-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes sb-pulse { 0%, 100% { opacity: .35; } 50% { opacity: .95; } }
@keyframes sb-live { 0%, 100% { box-shadow: 0 0 0 0 rgba(168,85,247,.7); } 50% { box-shadow: 0 0 0 6px rgba(168,85,247,0); } }
@keyframes sb-shine { from { transform: translateX(-120%) skewX(-18deg); } to { transform: translateX(320%) skewX(-18deg); } }
@keyframes sb-charge { 0% { box-shadow: 0 0 0 0 var(--glow); } 40% { box-shadow: 0 0 46px 4px var(--glow); } 100% { box-shadow: 0 0 0 0 var(--glow); } }
@keyframes sb-tape { 0% { opacity: 0; transform: translate(-50%, -50%) rotate(-9deg) scaleX(.2); } 100% { opacity: 1; transform: translate(-50%, -50%) rotate(-9deg) scaleX(1); } }
@keyframes sb-crown { 0% { opacity: 0; transform: translateY(-18px) scale(.6) rotate(-12deg); } 70% { opacity: 1; transform: translateY(2px) scale(1.08) rotate(4deg); } 100% { opacity: 1; transform: none; } }
@keyframes sb-tier { 0% { opacity: 0; transform: scale(.4); letter-spacing: 12px; } 60% { opacity: 1; transform: scale(1.06); } 100% { opacity: 1; transform: scale(1); } }
@keyframes sb-coin { 0% { opacity: 0; transform: translate(0, 0) rotateY(0deg); } 8% { opacity: 1; } 100% { opacity: .9; transform: translate(var(--dx), var(--dy)) rotateY(var(--spin)); } }
@keyframes sb-glint { from { opacity: 1; transform: translate(0, 0) scale(1); } to { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(.2); } }
@keyframes sb-ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes sb-aura { 0%, 100% { opacity: .55; transform: scale(1); } 50% { opacity: .9; transform: scale(1.06); } }
@keyframes sb-count { from { transform: scale(1.14); color: ${C.lavLight}; } to { transform: scale(1); } }
.sb-ease { animation-timing-function: cubic-bezier(.22, 1.2, .36, 1) !important; }
.sb-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${C.violet}; animation: sb-live 1.8s ease-in-out infinite; flex-shrink: 0; }
.sb-duel { display: flex; flex-direction: column; gap: 10px; }
@media (min-width: 768px) {
  .sb-duel { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); column-gap: 14px; row-gap: 8px; }
  .sb-duel-side { display: grid !important; grid-template-rows: subgrid; }
}
.sb-seg { display: flex; gap: 4px; padding: 4px; border-radius: 999px; border: 1px solid rgba(168,85,247,0.25); background: rgba(255,255,255,0.02); overflow-x: auto; max-width: 100%; box-sizing: border-box; }
.sb-seg::-webkit-scrollbar { display: none; }
.sb-grid { display: grid; gap: 8px; grid-template-columns: minmax(0, 1fr); }
@media (min-width: 640px) { .sb-grid.sb-2, .sb-grid.sb-3, .sb-grid.sb-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1000px) { .sb-grid.sb-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); } .sb-grid.sb-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
.sb-split { display: grid; gap: 16px; grid-template-columns: minmax(0, 1fr); }
@media (min-width: 900px) { .sb-split { grid-template-columns: minmax(0, 1fr) 260px; align-items: stretch; } }
.sb-plinths { display: flex; align-items: flex-end; justify-content: center; gap: 10px; padding-top: 24px; }
.sb-plinths > * { flex: 1 1 0; min-width: 0; max-width: 240px; }
.sb-hover { transition: border-color 150ms, background 150ms; }
.sb-hover:hover { border-color: rgba(168,85,247,0.5) !important; background: rgba(168,85,247,0.06) !important; }
.sb-btn:disabled { opacity: .4; cursor: not-allowed; }
.sb-scroll::-webkit-scrollbar { width: 6px; } .sb-scroll::-webkit-scrollbar-thumb { background: rgba(168,85,247,.25); border-radius: 3px; }
`;

// Rendered once by the page and once by the widget (their roots).
export const BattleStyles = () => <style>{BATTLE_CSS}</style>;

// ---------- shared inline styles ----------
export const panel = {
  background: 'linear-gradient(180deg, rgba(168,85,247,0.05), rgba(255,255,255,0.015))',
  border: `1px solid ${C.violetLine}`, borderRadius: '20px', padding: '1.25rem', position: 'relative',
};
export const label = {
  fontFamily: C.display, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.lav,
};
// kept for the console's small captions
export const eyebrow = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.4px', color: C.muted };
export const field = {
  padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(168,85,247,0.25)',
  borderRadius: '12px', color: '#fff', fontSize: '0.85rem', fontFamily: C.font, boxSizing: 'border-box', width: '100%', outline: 'none',
};
export function btn(kind = 'ghost', size = 'md') {
  const pad = size === 'sm' ? '0.3rem 0.75rem' : size === 'lg' ? '0.75rem 1.6rem' : '0.5rem 1.05rem';
  const fs = size === 'sm' ? '0.72rem' : size === 'lg' ? '0.92rem' : '0.82rem';
  const kinds = {
    // the one gold element on a screen: the call to action
    primary: { background: `linear-gradient(135deg, ${C.gold}, ${C.goldDeep})`, border: '1px solid transparent', color: C.onGold, boxShadow: '0 6px 20px -8px rgba(251,191,36,0.7)' },
    violet: { background: `linear-gradient(135deg, ${C.violet}, ${C.violetDeep})`, border: '1px solid transparent', color: '#fff', boxShadow: '0 6px 20px -8px rgba(168,85,247,0.8)' },
    green: { background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.4)', color: C.mint },
    danger: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.32)', color: '#fca5a5' },
    ghost: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(168,85,247,0.22)', color: C.bright },
    on: { background: 'rgba(168,85,247,0.18)', border: `1px solid ${C.violet}`, color: '#fff' },
  };
  return {
    ...kinds[kind], padding: pad, fontSize: fs, fontWeight: 700, borderRadius: '999px', cursor: 'pointer',
    fontFamily: C.font, lineHeight: 1.2, whiteSpace: 'nowrap',
  };
}
export const Btn = ({ kind = 'ghost', size = 'md', style, ...p }) => (
  <button type="button" className="sb-btn" {...p} style={{ ...btn(kind, size), ...style }} />
);
export const LiveDot = ({ size = 8 }) => <span className="sb-dot" style={{ width: size, height: size }} />;

// A name's initial in a violet disc — the site's avatar stand-in for chatters.
export function Initial({ name, size = 28 }) {
  const ch = (name || '?').replace(/[^a-z0-9]/gi, '').charAt(0).toUpperCase() || '?';
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: C.display, fontWeight: 800, fontSize: size * 0.42, color: '#fff', background: `linear-gradient(140deg, ${C.violet}, ${C.violetDeep})`, boxShadow: '0 0 0 2px rgba(168,85,247,0.25)' }}>
      {ch}
    </span>
  );
}

// The slot catalog (App's allSlots), for art when a slot carries a name but
// no image (demo states, older rows).
export const BattleCatalogContext = createContext([]);

// ---------- easing ----------
// cubic-bezier(x1, y1, x2, y2) as a function of progress, like CSS's.
export function cubicBezier(x1, y1, x2, y2) {
  const ax = 3 * x1 - 3 * x2 + 1; const bx = 3 * x2 - 6 * x1; const cx = 3 * x1;
  const ay = 3 * y1 - 3 * y2 + 1; const by = 3 * y2 - 6 * y1; const cy = 3 * y1;
  const sx = (t) => ((ax * t + bx) * t + cx) * t;
  const sy = (t) => ((ay * t + by) * t + cy) * t;
  const dx = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const err = sx(t) - x;
      if (Math.abs(err) < 1e-6) break;
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    t = Math.min(1, Math.max(0, t));
    return sy(t);
  };
}

// ---------- the draw strip ----------
// Case-opening style: a row of pool entries slides sideways under a fixed
// pointer, decelerates, drifts a touch past the drawn player and settles back
// onto them. Shared by the page and the widget. Timing lives in ROLL /
// rollSchedule() (battleLib.js).
//
// Every screen shows the same frame at the same moment: the strip is seeded
// by the play (`seed`), and the motion is a function of time since the draw —
// `total` is the strip's whole run from rolled_at, `spin` what's left of it.

// a small seeded PRNG (mulberry32), so a seed always gives the same strip
function seeded(seed) {
  let a = (Number(seed) || 1) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildStrip(pool, target, land, seed) {
  // sorted, so two screens whose pools arrived in a different order agree
  const others = pool.filter((e) => e.name !== target.name).sort((p, q) => (p.name < q.name ? -1 : p.name > q.name ? 1 : 0));
  const src = others.length ? others : [target];
  const rand = seeded(seed);
  const pick = () => src[Math.floor(rand() * src.length)];
  const strip = Array.from({ length: land + 5 }, pick);
  strip[land] = target;
  // no back-to-back repeats, so the strip reads as moving even in a tiny pool
  for (let i = 1; i < strip.length; i++) {
    if (i !== land && src.length > 1 && strip[i].name === strip[i - 1].name) strip[i] = pick();
  }
  return strip;
}

// the strip's path over its whole run (in cards): quick start, a long glide
// that drifts a third of a card past the target (93% of the run), then a
// gentle settle back
const EASE_RUN = cubicBezier(0.16, 0.7, 0.18, 1);
const EASE_BACK = cubicBezier(0.45, 0, 0.35, 1);
const OVER = 0.93;
function stripCards(p, land) {
  const past = land + 0.33;
  if (p <= OVER) return past * EASE_RUN(p / OVER);
  return past - 0.33 * EASE_BACK((p - OVER) / (1 - OVER));
}

export function DrawStrip({ pool, target, spin, total = spin, seed, cardW, height, gap = 8, renderCard, accent = C.violet }) {
  const [land] = useState(() => Math.max(18, Math.min(48, Math.round(total * 10))));
  const [strip] = useState(() => buildStrip(pool, target, land, seed ?? target.name.length));
  const [p0] = useState(() => Math.min(0.99, Math.max(0, (total - spin) / total)));
  const box = useRef(null);
  const inner = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => { if (box.current) setWidth(box.current.clientWidth); }, []);
  const step = cardW + gap;
  const xAt = (cards) => width / 2 - (cards * step + cardW / 2);

  useEffect(() => {
    if (!width) return undefined;
    const el = inner.current;
    const t0 = performance.now();
    let raf;
    let lastX = null;
    let lastT = t0;
    const tick = (now) => {
      const k = Math.min(1, (now - t0) / (spin * 1000));
      const x = xAt(stripCards(p0 + (1 - p0) * k, land));
      const dt = now - lastT;
      const speed = lastX == null || dt <= 0 ? 0 : (Math.abs(x - lastX) / dt) * 1000; // px/s
      lastX = x;
      lastT = now;
      const blur = Math.min(3, speed / (step * 14));
      if (el) {
        el.style.transform = `translate3d(${x}px, 0, 0)`;
        el.style.filter = blur > 0.08 ? `blur(${blur.toFixed(2)}px)` : 'none';
      }
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // one run per mount — re-renders must never restart it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const mask = 'linear-gradient(90deg, transparent 0%, #000 18%, #000 82%, transparent 100%)';
  return (
    <div ref={box} style={{ position: 'relative', height, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, maskImage: mask, WebkitMaskImage: mask }}>
        <div ref={inner} style={{ display: 'flex', gap, height: '100%', alignItems: 'center', willChange: 'transform', transform: `translate3d(${xAt(stripCards(p0, land))}px, 0, 0)` }}>
          {strip.map((entry, i) => (
            <div key={i} style={{ width: cardW, flexShrink: 0 }}>{renderCard(entry, i === land)}</div>
          ))}
        </div>
      </div>
      {/* the pointer */}
      <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, marginLeft: -1, background: accent, boxShadow: `0 0 12px ${accent}` }} />
      <div style={{ position: 'absolute', left: '50%', top: 0, marginLeft: -7, width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: `9px solid ${accent}` }} />
      <div style={{ position: 'absolute', left: '50%', bottom: 0, marginLeft: -7, width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderBottom: `9px solid ${accent}` }} />
    </div>
  );
}

// ---------- slot art ----------
// Shuffle card art, or a quiet placeholder carrying the pick's text — an
// unmatched chat pick has no art but still needs to read as "a slot".
export function useSlotImage(slot) {
  const catalog = useContext(BattleCatalogContext);
  if (slot?.image) return slot.image;
  if (slot?.name && catalog?.length) return findSlotArt(catalog, slot.name)?.image || null;
  return null;
}

export function SlotArt({ slot, width = '100%', radius = 10, style, eager = false, hidden = false, textSize }) {
  const [broken, setBroken] = useState(false);
  const url = useSlotImage(slot);
  const box = { width, aspectRatio: '3 / 4', borderRadius: radius, display: 'block', boxSizing: 'border-box', flexShrink: 0, ...style };
  if (hidden) {
    return (
      <div style={{ ...box, border: '1px dashed rgba(168,85,247,0.5)', background: 'radial-gradient(circle at 50% 40%, rgba(168,85,247,0.18), rgba(0,0,0,0.5))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: textSize || '1.2rem', opacity: 0.8 }}>🔒</span>
      </div>
    );
  }
  if (!url || broken) {
    return (
      <div
        title={slotLabel(slot) || 'No pick'}
        style={{
          ...box, position: 'relative', overflow: 'hidden', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          border: '1px solid rgba(168,85,247,0.3)', background: 'linear-gradient(170deg, rgba(168,85,247,0.25), rgba(5,5,7,0.9))',
        }}
      >
        <span style={{ fontSize: textSize || '0.58rem', fontWeight: 700, lineHeight: 1.2, color: C.lavLight, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>
          {slotLabel(slot) || '🎰'}
        </span>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={slot?.name || ''}
      loading={eager ? 'eager' : 'lazy'}
      onError={() => setBroken(true)}
      style={{ ...box, objectFit: 'cover', boxShadow: '0 10px 24px -12px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.06)' }}
    />
  );
}

// The slot name, with the raw chat text underneath when it was corrected to
// something else (so "goo" → Gates of Olympus is visible, not magic).
export function SlotName({ slot, size = '0.74rem' }) {
  if (!slot) return <span style={{ display: 'block', fontSize: size, color: C.faint }}>no slot yet</span>;
  const corrected = slot.name && slot.raw && slot.raw.toLowerCase() !== slot.name.toLowerCase();
  const line = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <span style={{ display: 'block', minWidth: 0 }}>
      <span style={{ ...line, fontSize: size, color: slot.name ? C.bright : C.muted, fontStyle: slot.name ? 'normal' : 'italic' }}>
        {slot.name || `“${slot.raw}”`}
      </span>
      {corrected && <span style={{ ...line, fontSize: '0.62rem', color: C.dim }}>chat said “{slot.raw}”</span>}
      {!slot.name && <span style={{ ...line, fontSize: '0.62rem', color: 'rgba(248,113,113,0.85)' }}>not found on Shuffle</span>}
    </span>
  );
}

export function Multi({ value, size = '0.85rem', style }) {
  return <span style={{ fontFamily: C.display, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: multiColor(value), fontSize: size, letterSpacing: '-0.3px', ...style }}>{fmtMulti(value)}</span>;
}

// Who's ahead: one bar split by the two sides' scores (50/50 until a side
// has a result). Shared by the page's duel card and the widget.
export function MomentumBar({ a, b, height = 8, showValues = false, fontSize = '0.8rem' }) {
  const sa = a ?? 0;
  const sb = b ?? 0;
  const share = sa + sb > 0 ? sa / (sa + sb) : 0.5;
  const pct = Math.max(4, Math.min(96, share * 100));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
      {showValues && <Multi value={a} size={fontSize} style={{ minWidth: '3.8rem' }} />}
      <div style={{ position: 'relative', flex: 1, height, borderRadius: 999, overflow: 'hidden', background: 'rgba(203,213,225,0.85)' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: `linear-gradient(90deg, ${C.violetDeep}, ${C.violet})`, boxShadow: '0 0 14px rgba(168,85,247,0.7)', transition: 'width 900ms cubic-bezier(.22,1.2,.36,1)' }} />
        <div style={{ position: 'absolute', left: `${pct}%`, top: -2, bottom: -2, width: 3, marginLeft: -1.5, background: '#fff', boxShadow: '0 0 8px #fff', transition: 'left 900ms cubic-bezier(.22,1.2,.36,1)' }} />
      </div>
      {showValues && <Multi value={b} size={fontSize} style={{ minWidth: '3.8rem', textAlign: 'right' }} />}
    </div>
  );
}

// ---------- a duel ----------
function StripCard({ entry }) {
  return (
    <div style={{ borderRadius: 10, padding: 4, background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(168,85,247,0.25)' }}>
      <SlotArt slot={entry.slot} radius={7} eager />
      <span style={{ display: 'block', marginTop: 4, fontSize: '0.62rem', fontWeight: 700, color: '#fff', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
    </div>
  );
}

// One bonus buy inside a duel. `spin` (a fresh draw) runs the draw strip
// through the pool first — same strip and timing as the stream widget.
function PlayTile({ play, dim, spin, total, pool, accent, children }) {
  const [landed, setLanded] = useState(!spin);
  useEffect(() => {
    if (landed) return undefined;
    const t = setTimeout(() => setLanded(true), spin * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const tile = { position: 'relative', borderRadius: '14px', padding: '0.65rem', background: 'rgba(5,5,7,0.55)', border: '1px solid rgba(255,255,255,0.06)', boxSizing: 'border-box' };
  if (!landed) {
    return (
      <div style={{ ...tile, padding: '0.4rem 0' }}>
        <DrawStrip pool={pool} target={{ name: play.name, slot: play.slot }} spin={spin} total={total} seed={play.id} cardW={62} height={112} accent={accent}
          renderCard={(entry) => <StripCard entry={entry} />} />
      </div>
    );
  }
  return (
    <div
      className={spin ? 'sb-ease' : undefined}
      style={{ ...tile, display: 'flex', alignItems: 'center', gap: '0.75rem', transition: 'opacity 400ms, filter 400ms', opacity: dim ? 0.42 : 1, filter: dim ? 'grayscale(0.9)' : 'none', animation: spin ? 'sb-pop 0.5s both' : undefined }}
    >
      {spin && <span style={{ position: 'absolute', inset: -1, borderRadius: '14px', border: `2px solid ${accent}`, pointerEvents: 'none', animation: 'sb-halo 0.7s ease-out forwards' }} />}
      <SlotArt slot={play.slot} width={50} radius={8} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', fontWeight: 800, color: '#fff', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{play.name}</span>
          {play.is_streamer && <span style={{ ...label, fontSize: '0.5rem', color: '#fff', background: C.violet, borderRadius: 999, padding: '1px 6px' }}>host</span>}
        </span>
        <SlotName slot={play.slot} />
        {play.buy != null && (
          <span style={{ display: 'block', marginTop: '0.2rem', fontSize: '0.66rem', color: C.dim, fontVariantNumeric: 'tabular-nums' }}>
            {fmtAmount(play.buy)} → {fmtAmount(play.payout)}
          </span>
        )}
        {children}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0, alignSelf: 'flex-start', paddingTop: '0.1rem' }}>
        {play.buy != null
          ? <Multi value={play.multi} size="1.05rem" />
          : <span style={{ ...label, fontSize: '0.52rem', color: C.dim, animation: 'sb-pulse 2s ease-in-out infinite' }}>awaiting bonus</span>}
      </div>
    </div>
  );
}

// A whole duel: side a against side b. `labels` names the sides (a player,
// "<first drawn>'s squad", the streamer). `renderPlay` lets the admin console
// hang its controls under each play. Pass `clock` (useServerClock) and `pool`
// (state.entries) to play the draw on a freshly drawn match — key the card on
// match.id so each draw mounts its own.
export function FightCard({ match, labels, renderPlay, header, footer, clock, pool }) {
  const { a, b } = sidesOf(match);
  const done = match.status === 'done';
  const score = { a: done ? match.a_score : sideScore(a), b: done ? match.b_score : sideScore(b) };
  const squad = match.team_size > 1;
  // fixed at mount: a re-render mid-draw must not re-time the strips
  const [sched] = useState(() => (clock ? rollSchedule(match, clock(match.rolled_at)) : null));
  const [reelPool] = useState(() => (pool || []).map((e) => ({ name: e.name, slot: e.slot })));
  const [rolling, setRolling] = useState(!!sched);
  useEffect(() => {
    if (!sched) return undefined;
    const t = setTimeout(() => setRolling(false), sched.end * 1000 + 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Side by side (md+), the two sides share one set of row tracks (subgrid):
  // header, one row per play, squad average — so player 2 on the left lines
  // up with player 2 on the right whatever each tile carries.
  const rows = 1 + Math.max(a.length, b.length, 1) + (squad ? 1 : 0);

  const side = (key, plays, col) => {
    const won = done && match.winner_side === key;
    const lost = done && match.winner_side !== key;
    // side labels are player names — don't spoil the draw
    const name = rolling && sched.sides[key] > 0 ? 'drawing…' : labels?.[key];
    return (
      <div
        className="sb-duel-side"
        style={{
          gridRow: `1 / span ${rows}`, gridColumn: col, position: 'relative', display: 'flex', flexDirection: 'column', gap: '0.5rem',
          minWidth: 0, borderRadius: '18px', padding: '0.75rem', transition: 'border-color 400ms, box-shadow 400ms',
          border: `1px solid ${won ? C.violet : 'rgba(255,255,255,0.07)'}`,
          background: won ? 'linear-gradient(180deg, rgba(168,85,247,0.14), rgba(168,85,247,0.03))' : 'rgba(255,255,255,0.02)',
          boxShadow: won ? '0 0 30px -10px rgba(168,85,247,0.8)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: SIDE[key], flexShrink: 0, transform: 'rotate(45deg)' }} />
          <span style={{ fontFamily: C.display, fontSize: '0.72rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{name}</span>
          {won && <span style={{ ...label, fontSize: '0.54rem', color: '#fff', background: C.violet, borderRadius: 999, padding: '0.15rem 0.55rem', flexShrink: 0 }}>👑 winner</span>}
          {lost && <span style={{ ...label, fontSize: '0.54rem', color: C.dim, flexShrink: 0 }}>{match.forfeit ? 'walkover' : 'busted'}</span>}
        </div>
        {plays.map((p) => (
          <PlayTile key={p.id} play={p} dim={lost} spin={sched?.spins[p.id]} total={sched?.totals[p.id]} pool={reelPool} accent={SIDE[key]}>
            {renderPlay?.(p)}
          </PlayTile>
        ))}
        {!plays.length && (
          <div style={{ borderRadius: '14px', border: '1px dashed rgba(168,85,247,0.25)', padding: '1rem', textAlign: 'center', fontSize: '0.74rem', color: C.dim }}>
            decided by the previous match
          </div>
        )}
        {squad && (
          <div style={{ gridRow: '-2 / -1', marginTop: 'auto', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0.35rem 0.25rem 0' }}>
            <span style={{ ...label, color: C.muted }}>squad average</span>
            <Multi value={score[key]} size="1.35rem" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ ...panel, padding: '1rem' }}>
      {typeof header === 'function' ? header({ rolling }) : header}
      {rolling ? (
        <p style={{ ...label, color: C.lav, margin: '0 0 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          <LiveDot size={7} /> drawing from the lineup
        </p>
      ) : (
        <div style={{ marginBottom: '0.75rem' }}>
          <MomentumBar a={score.a} b={score.b} showValues height={7} />
        </div>
      )}
      <div className="sb-duel" style={{ gridTemplateRows: `repeat(${rows}, auto)` }}>
        {side('a', a, 1)}
        {side('b', b, 2)}
      </div>
      {match.prize_amount > 0 && (
        <p style={{ margin: '0.75rem 0 0', textAlign: 'center', fontSize: '0.76rem', color: C.muted }}>
          On the line: <strong style={{ color: C.gold }}>{fmtAmount(match.prize_amount)}</strong>
          {match.team_size > 1
            ? (match.plays.some((p) => p.is_streamer) ? ", shared by chat's squad if they win" : ', shared by the winning squad')
            : ''}
        </p>
      )}
      {footer}
    </div>
  );
}

// A lineup entry: who, and what they picked.
export function EntryChip({ entry, rolled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', padding: '0.45rem 0.55rem 0.45rem 0.5rem', opacity: rolled ? 0.5 : 1 }}>
      <Initial name={entry.name} size={30} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: '0.84rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
        <span style={{ display: 'block', fontSize: '0.68rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slotLabel(entry.slot)}</span>
      </div>
      {rolled ? <span style={{ ...label, fontSize: '0.5rem', color: C.lav }}>drawn</span> : <SlotArt slot={entry.slot} width={24} radius={4} />}
    </div>
  );
}

export function PrizeLadder({ prizes, compact = false }) {
  const list = (prizes || []).filter((p) => p > 0);
  if (!list.length) return null;
  const medal = ['🥇', '🥈', '🥉'];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: compact ? 0 : '0.75rem' }}>
      {list.map((p, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', borderRadius: '10px', padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' }}>
          <span style={{ fontSize: '0.8rem' }}>{medal[i] || <span style={{ ...label, fontSize: '0.56rem' }}>#{i + 1}</span>}</span>
          <strong style={{ color: C.gold }}>{fmtAmount(p)}</strong>
        </span>
      ))}
    </div>
  );
}

// ---------- catalog search ----------
// Type-ahead over the site's slot catalog, for the streamer's own pick and
// for fixing a viewer's. Catalog slot → the shape the battle API uses.
export const catalogSlot = (s) => ({ key: s.slug, name: s.name, provider: s.provider, image: s.image });

export function SlotSearch({ onPick, placeholder = 'Search Shuffle slots…', allowCustom = true, autoFocus = false }) {
  const catalog = useContext(BattleCatalogContext);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const results = useMemo(() => (q.trim().length >= 2 ? searchSlots(catalog || [], q, 8) : []), [catalog, q]);
  const custom = allowCustom && q.trim().length >= 2;
  const total = results.length + (custom ? 1 : 0);

  const pick = (i) => {
    if (i < results.length) onPick({ slot: catalogSlot(results[i]) });
    else if (custom) onPick({ text: q.trim() });
    setQ('');
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={q}
        autoFocus={autoFocus}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || !total) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => (h + 1) % total); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (h - 1 + total) % total); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(hi); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        placeholder={catalog?.length ? placeholder : 'Loading the catalog…'}
        style={field}
      />
      {open && total > 0 && (
        <div className="sb-scroll" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 60, maxHeight: '20rem', overflowY: 'auto', borderRadius: '14px', border: '1px solid rgba(168,85,247,0.35)', background: 'rgba(10,8,16,0.98)', padding: '4px', boxShadow: '0 16px 40px rgba(0,0,0,0.6)' }}>
          {results.map((s, i) => (
            <button
              key={`${s.slug}:${s.name}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(i)}
              onMouseEnter={() => setHi(i)}
              style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '0.6rem', padding: '0.35rem 0.5rem', borderRadius: '10px', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: C.font, background: hi === i ? 'rgba(168,85,247,0.15)' : 'transparent' }}
            >
              <SlotArt slot={catalogSlot(s)} width={26} radius={4} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                <span style={{ display: 'block', fontSize: '0.66rem', color: C.lav }}>{s.provider}</span>
              </span>
            </button>
          ))}
          {custom && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(results.length)}
              onMouseEnter={() => setHi(results.length)}
              style={{ display: 'block', width: '100%', padding: '0.5rem', borderRadius: '10px', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: C.font, fontSize: '0.72rem', color: C.muted, background: hi === results.length ? 'rgba(168,85,247,0.15)' : 'transparent' }}
            >
              Use “{q.trim()}” as typed (not on Shuffle)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Esc-to-close overlay, used for the history detail and single-match views.
export function Modal({ onClose, children, wide = false }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 9600, overflowY: 'auto', background: 'rgba(5,5,7,0.88)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ position: 'relative', width: '100%', maxWidth: wide ? '1150px' : '820px', paddingTop: '2.75rem', paddingBottom: '2rem' }}>
        <button type="button" onClick={onClose} style={{ ...btn('ghost', 'sm'), position: 'absolute', right: 0, top: 0 }}>✕ Close</button>
        {children}
      </div>
    </div>
  );
}
